import {
  Component,
  Suspense,
  lazy,
  type CSSProperties,
  type ReactNode,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { CodeBlockRenderer } from "../types";

interface MarkdownRendererProps {
  content: string;
  codeBlockRenderers?: Record<string, CodeBlockRenderer>;
  onCodeBlockRenderError?: (language: string, cause: Error) => void;
}

/**
 * Markdown 渲染（含语法高亮 + 自定义 code block 渲染器）
 *
 * - syntax-highlighter 通过 React.lazy 异步加载，仅在出现 fenced code block 时下载
 * - 自定义渲染器通过 ErrorBoundary 隔离，渲染异常自动降级为默认语法高亮
 */
export default function MarkdownRenderer({
  content,
  codeBlockRenderers,
  onCodeBlockRenderError,
}: MarkdownRendererProps) {
  const components: Components = {
    code({ className, children, ...rest }) {
      const match = /language-(\w+)/.exec(className ?? "");
      const language = match ? match[1].toLowerCase() : "";
      const codeText = String(children ?? "").replace(/\n$/, "");
      const isFenced = !!language || codeText.includes("\n");

      if (!isFenced) {
        return (
          <code className={className} style={inlineCodeStyle} {...rest}>
            {children}
          </code>
        );
      }

      const Custom = language ? codeBlockRenderers?.[language] : undefined;
      if (Custom) {
        return (
          <CodeBlockBoundary
            language={language}
            onError={onCodeBlockRenderError}
            fallback={<DefaultCodeBlock code={codeText} language={language} />}
          >
            <Custom code={codeText} language={language} />
          </CodeBlockBoundary>
        );
      }

      return <DefaultCodeBlock code={codeText} language={language} />;
    },

    table({ children }) {
      return (
        <div style={{ overflow: "auto", margin: "8px 0" }}>
          <table style={tableStyle}>{children}</table>
        </div>
      );
    },
    th: ({ children }) => <th style={thStyle}>{children}</th>,
    td: ({ children }) => <td style={tdStyle}>{children}</td>,

    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "var(--lude-im-accent)" }}
      >
        {children}
      </a>
    ),

    p: ({ children }) => <p style={{ margin: "6px 0", lineHeight: 1.6 }}>{children}</p>,
    ul: ({ children }) => <ul style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ol>,

    h1: ({ children }) => (
      <h1 style={{ fontSize: 18, fontWeight: 600, margin: "12px 0 6px" }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: "10px 0 4px" }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: "8px 0 4px" }}>{children}</h3>
    ),

    blockquote: ({ children }) => (
      <blockquote style={blockquoteStyle}>{children}</blockquote>
    ),

    hr: () => (
      <hr
        style={{
          border: "none",
          borderTop: "1px solid var(--lude-im-border)",
          margin: "12px 0",
        }}
      />
    ),
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}

// ─── 默认代码块（含 lazy 语法高亮） ─────────────────────────────────────────

const SyntaxHighlighterLazy = lazy(() =>
  Promise.all([
    import("react-syntax-highlighter"),
    import("react-syntax-highlighter/dist/esm/styles/prism"),
  ]).then(([sh, themes]) => ({
    default: function LazyHL({
      code,
      language,
    }: {
      code: string;
      language: string;
    }) {
      const Prism = sh.Prism;
      return (
        <Prism style={themes.oneDark} language={language || "text"} customStyle={codeBlockStyle}>
          {code}
        </Prism>
      );
    },
  })),
);

function DefaultCodeBlock({ code, language }: { code: string; language: string }) {
  return (
    <div style={{ position: "relative", margin: "8px 0" }}>
      {language && <div style={langLabelStyle}>{language}</div>}
      <button
        style={copyBtnStyle}
        onClick={() => {
          void navigator.clipboard.writeText(code);
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.5";
        }}
      >
        Copy
      </button>
      <Suspense fallback={<pre style={fallbackPreStyle}>{code}</pre>}>
        <SyntaxHighlighterLazy code={code} language={language} />
      </Suspense>
    </div>
  );
}

// ─── ErrorBoundary 隔离自定义渲染器 ──────────────────────────────────────────

interface BoundaryProps {
  language: string;
  fallback: ReactNode;
  onError?: (language: string, cause: Error) => void;
  children: ReactNode;
}

interface BoundaryState {
  errored: boolean;
}

class CodeBlockBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { errored: false };

  static getDerivedStateFromError(): BoundaryState {
    return { errored: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(this.props.language, error);
  }

  render() {
    if (this.state.errored) return this.props.fallback;
    return this.props.children;
  }
}

// ─── 样式 ────────────────────────────────────────────────────────────────────

const codeBlockStyle: CSSProperties = {
  borderRadius: 8,
  fontSize: 12,
  padding: "30px 14px 12px",
  margin: 0,
  overflowX: "auto",
  maxWidth: "100%",
};

const fallbackPreStyle: CSSProperties = {
  ...codeBlockStyle,
  background: "#282c34",
  color: "#abb2bf",
  fontFamily: "monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflow: "auto",
};

const langLabelStyle: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 12,
  fontSize: 10,
  color: "var(--lude-im-text-secondary)",
  background: "#282c34",
  padding: "2px 8px",
  borderRadius: "0 0 4px 4px",
  zIndex: 1,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const copyBtnStyle: CSSProperties = {
  position: "absolute",
  top: 6,
  right: 8,
  fontSize: 11,
  color: "var(--lude-im-text-secondary)",
  background: "none",
  border: "none",
  cursor: "pointer",
  opacity: 0.5,
  zIndex: 1,
  transition: "opacity 0.15s",
};

const inlineCodeStyle: CSSProperties = {
  background: "rgba(127,127,127,0.15)",
  padding: "1px 5px",
  borderRadius: 4,
  fontSize: "0.9em",
  fontFamily: "monospace",
};

const tableStyle: CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  fontSize: 13,
};

const thStyle: CSSProperties = {
  border: "1px solid var(--lude-im-border)",
  padding: "6px 10px",
  background: "var(--lude-im-bg-secondary)",
  textAlign: "left",
  fontWeight: 600,
};

const tdStyle: CSSProperties = {
  border: "1px solid var(--lude-im-border)",
  padding: "6px 10px",
};

const blockquoteStyle: CSSProperties = {
  borderLeft: "3px solid var(--lude-im-accent)",
  paddingLeft: 12,
  margin: "8px 0",
  color: "var(--lude-im-text-secondary)",
};
